import React, { MouseEventHandler } from 'react';
import { useTheme } from 'styled-components';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import { formatFileSizeForDisplay } from '../../helpers/documentHelpers';
import { applyComponentTheme } from '../../theme';
import Icon from '../Icon';
import { IconSize } from '../Icon/styles';
import {
  ActionLinkStyle,
  DescriptionText,
  DocumentDescriptor,
  IconWrapper,
} from './styles';

export type DocumentDownloadLinkProps = {
  documentName: string;
  documentUrl: string;
  fileType?: string;
  fileSize?: number;
  iconName?: string;
  iconSize?: IconSize;
  openInNewTab?: boolean;
  openInNewTabScreenReaderText?: string;
  hideIcon?: boolean;
  descriptionText?: string;
  onClick?: MouseEventHandler;
} & ComponentProps;

const DocumentDownloadLink: React.FC<DocumentDownloadLinkProps> = ({
  documentName,
  documentUrl,
  fileType,
  fileSize,
  iconName,
  iconSize,
  openInNewTab,
  openInNewTabScreenReaderText = 'opens in a new tab',
  hideIcon,
  descriptionText,
  onClick,
  ...props
}) => {
  const theme = useTheme();

  const downloadDocumentIcon = iconName || theme.buttonIcons?.download;
  const formattedFileType = fileType?.toUpperCase();
  const formattedFileSize = formatFileSizeForDisplay(fileSize);

  const descriptor = [documentName, formattedFileType, formattedFileSize]
    .filter((item) => item)
    .join(' - ');

  return (
    <>
      <ActionLinkStyle
        href={documentUrl}
        aria-label={`${documentName}${
          openInNewTab ? ` (${openInNewTabScreenReaderText})` : ''
        }`}
        openInNewTab={openInNewTab}
        onClick={onClick}
        {...componentProps(props)}>
        {downloadDocumentIcon && !hideIcon && (
          <IconWrapper>
            <Icon name={downloadDocumentIcon} size={iconSize || 'medium'} />
          </IconWrapper>
        )}
        <DocumentDescriptor>{descriptor}</DocumentDescriptor>
      </ActionLinkStyle>
      {descriptionText && <DescriptionText>{descriptionText}</DescriptionText>}
    </>
  );
};

export default applyComponentTheme(DocumentDownloadLink, 'DocumentDownloadLink');
